import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableRenderProps,
} from '@carbon/react';
import type {
  TractNriMatrixBreakdownItem,
  NriMatrixResponseRow,
  NriMatrixFormulaInputs,
} from 'types/api-responses';
import styles from './tract-nri-breakdown-popup.module.scss';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { CalculationType } from 'components/nri-table/types';
import {
  formatValue,
  formatFactor,
  AllocationMethod,
} from './tract-nri-matrix-utils';
import { UatInterestCell } from './uat-interest-cell';

interface BreakdownItemWithMatch extends TractNriMatrixBreakdownItem {
  matchingRow?: NriMatrixResponseRow;
}

interface TractNriBreakdownPopupProps {
  breakdownItems: BreakdownItemWithMatch[];
  allocationMethod: 'acreage' | 'lateralLength';
  source: 'plat' | 'gis';
  calculationType: CalculationType;
  wellName?: string;
}

const getHeaders = (calculationType: CalculationType) => [
  { key: 'subtractName', header: 'Subtract' },
  { key: 'uatName', header: 'UAT' },
  { key: 'value', header: 'Value' },
  { key: 'factor', header: 'Factor' },
  {
    key: 'uatInterest',
    header: calculationType === 'WI' ? 'UAT WI' : 'UAT NRI',
  },
];

interface FormattedBreakdownRow {
  id: string;
  nriCalculationId: number;
  subtractName: string;
  uatName: string;
  value: number | null;
  factor: number | null;
  uatNri: number | null;
  uatWi: number | null;
  formulaInputs: NriMatrixFormulaInputs;
  allocationMethod: AllocationMethod;
  source: 'plat' | 'gis';
}

export const TractNriBreakdownPopup = ({
  breakdownItems,
  allocationMethod,
  calculationType,
  wellName,
}: TractNriBreakdownPopupProps) => {
  const headers = getHeaders(calculationType);

  const tableRows: FormattedBreakdownRow[] = breakdownItems
    .filter((item) => item.matchingRow)
    .map((item, index) => {
      const matchingRow = item.matchingRow!;
      return {
        id: String(index),
        nriCalculationId: item.nriCalculationId,
        subtractName: item.subtractName ?? '--',
        uatName: item.unitAllocationTract?.tractName ?? '--',
        value: matchingRow.value,
        factor: matchingRow.factor,
        uatNri: matchingRow.nri,
        uatWi: matchingRow.workingInterest,
        formulaInputs: item.formulaInputs,
        allocationMethod: matchingRow.allocationMethod,
        source: matchingRow.source,
      };
    });

  const renderUatInterestCell = (row: FormattedBreakdownRow) => {
    const interestValue = calculationType === 'WI' ? row.uatWi : row.uatNri;

    return (
      <UatInterestCell
        interestValue={interestValue}
        displayValue={formatNumberWithTooltip(interestValue ?? 0, 8)}
        formulaInputs={row.formulaInputs}
        allocationMethod={row.allocationMethod}
        source={row.source}
        rowValue={row.value}
        calculationType={calculationType}
        cellClassName={styles.uatNriCell}
        cardContentClassName={styles.matrixCardContent}
        iconContainerClassName={styles.iconContainer}
      />
    );
  };

  const titlePrefix =
    calculationType === 'WI' ? 'Total Slot WI' : 'Total Slot NRI';

  if (tableRows.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          {titlePrefix}
          {wellName && <span className={styles.subtitle}>({wellName})</span>}
        </div>
        <p>No breakdown data available for this allocation method.</p>
      </div>
    );
  }

  return (
    <div className={styles.container} data-portal-container>
      <div className={styles.title}>
        {titlePrefix}
        {wellName && <span className={styles.subtitle}>({wellName})</span>}
      </div>
      <div className={styles.tableWrapper}>
        <DataTable rows={tableRows} headers={headers}>
          {({
            rows,
            headers,
            getTableProps,
            getHeaderProps,
            getRowProps,
          }: DataTableRenderProps<any, any[]>) => (
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header: any) => (
                    <TableHeader
                      {...getHeaderProps({ header })}
                      key={header.key}
                    >
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: any) => {
                  const rowIndex = parseInt(row.id);
                  const rowData = tableRows[rowIndex];

                  return (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map((cell: any) => (
                        <TableCell key={cell.id}>
                          {cell.info.header === 'value'
                            ? formatValue(
                                rowData?.value ?? null,
                                allocationMethod
                              )
                            : cell.info.header === 'factor'
                              ? formatFactor(rowData?.factor ?? null)
                              : cell.info.header === 'uatInterest' && rowData
                                ? renderUatInterestCell(rowData)
                                : (cell.value ?? '--')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DataTable>
      </div>
    </div>
  );
};
